import { onboardingProgressApi } from '@/api/onboardingProgress.api';

/** Where everybody lands by default — not somewhere anybody specifically asked to go. */
const DEFAULT_LANDINGS = ['/dashboard', '/', '/login'];

/**
 * Where a signed-in person should land.
 *
 * <p>
 * The one answer, used after a sign-in AND when somebody who is already signed in opens the login
 * page — so the two can never disagree about where a workspace sends its people.
 * </p>
 * <ol>
 * <li>Somewhere SPECIFIC they were bounced from wins outright — they were doing something. The
 * dashboard is not somewhere specific: honouring it would skip the onboarding check below.</li>
 * <li>An unfinished onboarding goes back to onboarding. Asked of the server, because the answer has
 * to hold on a machine that has never seen this signup.</li>
 * <li>Everybody else: the dashboard.</li>
 * </ol>
 * <p>
 * A failure while asking falls through to the dashboard: this decides where to land, and must never
 * decide whether to.
 * </p>
 */
export async function resolveLandingPath(from?: string | null): Promise<string> {
  const path = from?.split(/[?#]/)[0] ?? null;
  if (from && path && !DEFAULT_LANDINGS.includes(path)) return from;

  try {
    const status = await onboardingProgressApi.status();
    if (status.status === 'InProgress') return '/onboarding';
  } catch {
    // A workspace with no setup record simply goes on to the dashboard.
  }

  return '/dashboard';
}
